import React, { useEffect } from 'react';
import { Analytics } from '@/utils/analytics';
import { BrandPageScreen } from '@/screens/brand-page/brand-page-screen';
import { CATALOG_ROUTES } from '@/navigators/routes';
import { CatalogCategoryListScreen } from '@/screens/partners/catalog-category-list-screen';
import { CatalogScanner } from '@/components/scanner/catalog-scanner';
import { CatalogScreen } from '@/screens/partners/catalog-screen';
import { CatalogSearchScreen } from '@/screens/partners/catalog-search-screen';
import { CatalogStackParams } from '@/navigators/types';
import { CatalogUnifiedSearchScreen } from '@/screens/partners/unified-search-screen';
import { CatalogVolumePromotionsListScreen } from '@/screens/partners/catalog-volume-promotions-list-screen';
import { createStackNavigator } from '@react-navigation/stack';
import { CreditRequestScanScreen } from '@/screens/partners/credit-request-scan-screen';
import { EnhancedCatalogScreen } from '@/screens/partners/enhanced-catalog-screen';
import { ItemRecommendationsListScreen } from '@/screens/partners/item-recommendations-list-screen';
import { SearchScreen } from '@/screens/partners/search-screen';
import { SearchSupplierCatalogScreen } from '@/screens/partners/search-supplier-catalog-screen';
import { TagPrintBatchScreen } from '@/screens/partners/tag-print-batch-screen';
import { useCatalogGroupItemsLoader } from '@/hooks/use-catalog-group-items-loader';
import { useDefaultAppTabScreenOptions } from '@/hooks/use-default-app-tab-screen-options';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useUserContext } from '@/contexts/user-context';
import { VolumePromotionDetailScreen } from '@/screens/partners/volume-promotion-detail-screen';

const Stack = createStackNavigator<CatalogStackParams>();

export const CatalogStack: React.FC = () => {
  const screenOptions = useDefaultAppTabScreenOptions();
  const { isGuestMode, selectedRestaurantRelationship } = useUserContext();

  const catalogSearchUXEnabled = useDynamicConfigValue(
    'catalog_search_ux_enabled',
  );

  useEffect(() => {
    // Ensure analytics is loaded for public catalog event tracking
    Analytics.load();
  }, []);

  useCatalogGroupItemsLoader();

  return (
    <Stack.Navigator screenOptions={screenOptions}>
      <Stack.Screen
        component={
          !selectedRestaurantRelationship.carouselHoldout || isGuestMode
            ? catalogSearchUXEnabled
              ? CatalogSearchScreen
              : EnhancedCatalogScreen
            : CatalogScreen
        }
        name={CATALOG_ROUTES.Base}
      />
      <Stack.Screen
        component={CatalogUnifiedSearchScreen}
        name={CATALOG_ROUTES.CatalogUnifiedSearch}
      />
      <Stack.Screen
        component={CatalogCategoryListScreen}
        name={CATALOG_ROUTES.CategoryList}
      />
      <Stack.Screen
        component={
          catalogSearchUXEnabled ? SearchScreen : SearchSupplierCatalogScreen
        }
        name={CATALOG_ROUTES.Search}
      />
      {isGuestMode ? null : (
        <>
          <Stack.Screen
            component={ItemRecommendationsListScreen}
            name={CATALOG_ROUTES.ItemRecommendationsList}
          />
          <Stack.Screen
            component={CatalogVolumePromotionsListScreen}
            name={CATALOG_ROUTES.VolumePromotionsList}
          />
          <Stack.Screen
            component={VolumePromotionDetailScreen}
            name={CATALOG_ROUTES.VolumePromotionDetail}
          />
          <Stack.Screen component={CatalogScanner} name={CATALOG_ROUTES.Scan} />
          <Stack.Screen
            component={CreditRequestScanScreen}
            name={CATALOG_ROUTES.CreditRequestScan}
          />
          <Stack.Screen
            component={BrandPageScreen}
            name={CATALOG_ROUTES.BrandPage}
          />
          <Stack.Screen
            component={TagPrintBatchScreen}
            name={CATALOG_ROUTES.TagPrintBatch}
          />
        </>
      )}
    </Stack.Navigator>
  );
};
